import { BoardEntry } from "services/highScores";

interface HighScoreBoardProps {
  title: string;
  // The full board from the hub (ordered, up to 50). Rank = position in this list.
  entries: BoardEntry[];
  highlightName?: string;
  // How many top rows to show; the current user's entries below this still show.
  displayLimit?: number;
}

type RankedEntry = BoardEntry & { rank: number };

export const HighScoreBoard = ({
  title,
  entries,
  highlightName,
  displayLimit = 20,
}: HighScoreBoardProps): JSX.Element => {
  const isMine = (e: BoardEntry) =>
    !!highlightName && e.name.toLowerCase() === highlightName.toLowerCase();

  const ranked: RankedEntry[] = entries.map((e, i) => ({ ...e, rank: i + 1 }));
  const top = ranked.slice(0, displayLimit);
  // The current user's entries that fall below the visible top-N.
  const extraForUser = ranked.slice(displayLimit).filter(isMine);

  const renderRow = (e: RankedEntry) => (
    <tr key={`${e.name}-${e.rank}`} className={isMine(e) ? "hs-board-row-mine" : ""}>
      <td className="hs-board-rank">{e.rank}</td>
      <td className="hs-board-name">{e.name}</td>
      <td className="hs-board-score">{e.score}</td>
      <td className="hs-board-date">{e.at}</td>
    </tr>
  );

  return (
    <div className="hs-board">
      <h4 className="hs-board-title">
        {title}
        <sup className="hs-board-note-mark">*</sup>
      </h4>
      {ranked.length === 0 ? (
        <div className="hs-board-empty">No scores yet.</div>
      ) : (
        <table className="hs-board-table">
          <tbody>
            {top.map(renderRow)}
            {extraForUser.length > 0 && (
              <tr className="hs-board-gap" key="gap">
                <td className="hs-board-rank">⋮</td>
                <td colSpan={3} />
              </tr>
            )}
            {extraForUser.map(renderRow)}
          </tbody>
        </table>
      )}
    </div>
  );
};
